// Visual-type → renderer registry. PageView looks up the renderer by
// spec.type and falls back to PlaceholderVisual for unknown types.

import type { VisualType } from "../types/pbi";
import type { VisualRenderer } from "./types";

import { ColumnChartVisual } from "./ColumnChartVisual";
import { BarChartVisual } from "./BarChartVisual";
import { LineChartVisual } from "./LineChartVisual";
import { AreaChartVisual } from "./AreaChartVisual";
import { DonutChartVisual } from "./DonutChartVisual";
import { TreemapVisual } from "./TreemapVisual";
import { RibbonChartVisual } from "./RibbonChartVisual";
import { GaugeVisual } from "./GaugeVisual";
import { ComboChartVisual } from "./ComboChartVisual";
import { CardVisual } from "./CardVisual";
import { TableExVisual } from "./TableExVisual";
import { PivotTableVisual } from "./PivotTableVisual";
import { SlicerVisual } from "./SlicerVisual";
import { MapVisual } from "./MapVisual";
import { FilledMapVisual } from "./FilledMapVisual";
import { TextboxVisual } from "./TextboxVisual";
import { ImageVisual } from "./ImageVisual";
import { ShapeVisual } from "./ShapeVisual";
import { ActionButtonVisual } from "./ActionButtonVisual";
import { BookmarkNavigatorVisual } from "./BookmarkNavigatorVisual";
import { PlaceholderVisual } from "./PlaceholderVisual";

const registry = new Map<string, VisualRenderer>();

export function registerVisualType(type: VisualType, renderer: VisualRenderer): void {
  registry.set(type, renderer);
}

export function getVisualRenderer(type: VisualType): VisualRenderer {
  return registry.get(type) ?? PlaceholderVisual;
}

export function hasVisualRenderer(type: VisualType): boolean {
  return registry.has(type);
}

// ── Default registrations for the 29 known PBI visual types ──────────────────
// Column-family
registerVisualType("columnChart", ColumnChartVisual);
registerVisualType("clusteredColumnChart", ColumnChartVisual);
registerVisualType("hundredPercentStackedColumnChart", ColumnChartVisual);
// Bar-family
registerVisualType("barChart", BarChartVisual);
registerVisualType("clusteredBarChart", BarChartVisual);
// Line / area
registerVisualType("lineChart", LineChartVisual);
registerVisualType("areaChart", AreaChartVisual);
// Combo
registerVisualType("lineClusteredColumnComboChart", ComboChartVisual);
registerVisualType("lineStackedColumnComboChart", ComboChartVisual);
// Other charts
registerVisualType("donutChart", DonutChartVisual);
registerVisualType("treemap", TreemapVisual);
registerVisualType("ribbonChart", RibbonChartVisual);
registerVisualType("gauge", GaugeVisual);
// Cards / tables
registerVisualType("card", CardVisual);
registerVisualType("cardVisual", CardVisual);
registerVisualType("tableEx", TableExVisual);
registerVisualType("pivotTable", PivotTableVisual);
// Slicer
registerVisualType("slicer", SlicerVisual);
// Maps
registerVisualType("map", MapVisual);
registerVisualType("filledMap", FilledMapVisual);
// Static elements
registerVisualType("textbox", TextboxVisual);
registerVisualType("image", ImageVisual);
registerVisualType("shape", ShapeVisual);
registerVisualType("basicShape", ShapeVisual);
registerVisualType("actionButton", ActionButtonVisual);
registerVisualType("bookmarkNavigator", BookmarkNavigatorVisual);
// Custom / unknown — placeholder
registerVisualType("unknown", PlaceholderVisual);
// SmartFilter (SQLBI custom visual) — placeholder for now
registerVisualType("SmartFilterBySQLBI1458262140625", PlaceholderVisual);
// Variance (custom visual, GUID suffix changes per report) — placeholder
registerVisualType("variance8E4BB1B41A8942A7B897C7014A6E1F56", PlaceholderVisual);
